// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@awc/App/Components/Skeleton/Skeleton";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";

// <XNG-Changes>
const enum fields {
    visibleContent = "visibleContent",
}

const enum visibleContent {
    showHtmlContent = "showHtmlContent",
    showCategoryDescription = "showCategoryDescription",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibleContent]: visibleContent;
    };
}

// XNG-Change
const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ categoryPath: string }>(location.search).categoryPath
            : "") ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const isLoading = getCatalogPageStateByPath(state, categoryPath)?.isLoading;

    return {
        isLoading,
    };
};
// </XNG-Changes>

interface Props extends OwnProps, HasCategoryContext {}

export interface CategoryDetailBannerStyles {
    container?: InjectableCss;
}

export const bannerStyles: CategoryDetailBannerStyles = {};

const styles = bannerStyles;

const CategoryDetailBanner = ({ category, fields, isLoading }: Props & ReturnType<typeof mapStateToProps>) => {
    if (isLoading) {
        return <Skeleton height="200px" />;
    }
    if (!category) {
        return null;
    }

    // XNG-Changes: choose content to render based on fields value
    // we have another custom content (category.properties.categoryDescription)
    const contentToRender =
        fields.visibleContent === visibleContent.showHtmlContent
            ? category.htmlContent
            : category.properties.categoryDescription;
    if (!contentToRender) {
        return null;
    }

    return (
        <StyledWrapper
            as="section"
            aria-labelledby="category-title"
            {...styles.container}
            data-test-selector="categoryDetailBanner_htmlContent"
        >
            {parse(transformContentHtml(contentToRender), parserOptions)}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryDetailBanner)),
    definition: {
        group: "Category Details",
        icon: "image-landscape",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.visibleContent,
                displayName: "Show HtmlContent or Custom Description",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Html Content",
                        value: visibleContent.showHtmlContent,
                    },
                    {
                        displayName: "Show Custom Description",
                        value: visibleContent.showCategoryDescription,
                    },
                ],
                defaultValue: visibleContent.showCategoryDescription,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
